import React, { useEffect } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './LessonContentRenderer.module.css';

interface FailureFeedbackProps {
    show: boolean;
    onComplete?: () => void;
}

const FailureFeedback: React.FC<FailureFeedbackProps> = ({ show, onComplete }) => {
    const [hasError, setHasError] = React.useState(false);

    // Auto-dismiss after 4 seconds as a fallback
    useEffect(() => {
        if (show && onComplete) {
            const timer = setTimeout(() => {
                onComplete();
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [show, onComplete]);

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className={`${styles.celebrationOverlay} ${styles.failureOverlay}`}
                    onClick={onComplete} // Allow click to dismiss
                >
                    <div className={styles.celebrationContainer} onClick={(e) => e.stopPropagation()}>
                        {!hasError ? (
                            <DotLottieReact
                                src="/assets/animations/failure-x.lottie" // A clean 'X' or 'Retry' animation
                                autoplay
                                loop={false}
                                dotLottieRefCallback={(dotLottie) => {
                                    if (dotLottie) {
                                        dotLottie.addEventListener('complete', () => {
                                            if (onComplete) onComplete();
                                        });
                                        dotLottie.addEventListener('loadError', () => {
                                            setHasError(true);
                                        });
                                    }
                                }}
                                className={styles.celebrationLottie}
                            />
                        ) : (
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1.5 }}
                                className={styles.celebrationLottie}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '5rem' }}
                            >
                                ❌
                            </motion.div>
                        )}

                        <motion.div
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.3, type: 'spring', stiffness: 200, damping: 10 }}
                            className={styles.celebrationText}
                        >
                            <span className={styles.celebrationEmoji}>🤔</span>
                            <h3>Let's Try Again</h3>
                            <p style={{ color: 'rgba(255,255,255,0.8)', marginTop: '0.5rem' }}>
                                Review the section above and give it another shot.
                            </p>
                        </motion.div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default FailureFeedback;
